import React from 'react';
import { Box, Typography, TextField, Select, MenuItem, FormControl, Chip } from '@mui/material';
import { colors, fonts, spacing } from '../../constants';
import { REGION_OPTIONS, STATUS_OPTIONS } from '../../constants/projectsPage';
import { getStatusColor } from '../../utils/projectsUtils';

/**
 * Shared form field styles
 */
const formFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#FAFAFA',
    fontSize: '15px',
    '& fieldset': {
      borderColor: '#E5E7EB',
    },
    '&:hover fieldset': {
      borderColor: '#D1D5DB',
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primary,
      borderWidth: '2px',
    },
  },
  '& .MuiInputBase-input': {
    padding: '12px 14px',
    fontSize: '15px',
    '&::placeholder': {
      color: '#9CA3AF',
      opacity: 1,
    },
  },
};

const selectFieldStyles = {
  borderRadius: '8px',
  backgroundColor: '#FAFAFA',
  fontSize: '15px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E5E7EB',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D1D5DB',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.primary,
    borderWidth: '2px',
  },
  '& .MuiSelect-select': {
    padding: '12px 14px',
    fontSize: '15px',
  },
};

const labelStyles = {
  mb: '6px',
  fontWeight: fonts.weightMedium,
  fontSize: '14px',
  color: colors.textPrimaryDark,
};

/**
 * FormFieldLabel Component
 * Reusable label component with optional required indicator
 */
const FormFieldLabel = ({ children, required = false }) => (
  <Typography
    sx={{
      ...labelStyles,
      display: required ? 'flex' : 'block',
      alignItems: required ? 'center' : 'flex-start',
    }}
  >
    {children}
    {required && <Box component="span" sx={{ color: colors.error, ml: '3px' }}>*</Box>}
  </Typography>
);

/**
 * AddProjectForm Component
 * Form component with all project fields
 */
const AddProjectForm = ({ formData, onInputChange, getStatusColor }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      {/* Project Name */}
      <Box>
        <FormFieldLabel required>Project Name</FormFieldLabel>
        <TextField
          fullWidth
          placeholder="Enter project name or use search above"
          name="name"
          value={formData.name}
          onChange={onInputChange}
          required
          sx={formFieldStyles}
        />
      </Box>

      {/* Region */}
      <Box>
        <FormFieldLabel required>Region</FormFieldLabel>
        <FormControl fullWidth required>
          <Select
            name="region"
            value={formData.region}
            onChange={onInputChange}
            displayEmpty
            sx={selectFieldStyles}
          >
            <MenuItem value="" disabled sx={{ color: '#9CA3AF', fontSize: '15px' }}>
              Select Region
            </MenuItem>
            {REGION_OPTIONS.map((region) => (
              <MenuItem key={region.value} value={region.value} sx={{ fontSize: '15px', py: '10px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={region.value}
                    size="small"
                    sx={{
                      backgroundColor: colors.primaryTint,
                      color: colors.primary,
                      fontWeight: fonts.weightBold,
                      fontSize: '11px',
                      height: '22px',
                    }}
                  />
                  <Typography sx={{ fontSize: '15px' }}>{region.label}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Project Code */}
      <Box>
        <FormFieldLabel required>Project Code</FormFieldLabel>
        <TextField
          fullWidth
          placeholder="e.g., HWY-EXP-02"
          name="code"
          value={formData.code}
          onChange={onInputChange}
          required
          sx={formFieldStyles}
        />
      </Box>

      {/* Description */}
      <Box>
        <FormFieldLabel>Description</FormFieldLabel>
        <TextField
          fullWidth
          placeholder="Enter project description"
          name="description"
          value={formData.description}
          onChange={onInputChange}
          multiline
          rows={3}
          sx={formFieldStyles}
        />
      </Box>

      {/* Status */}
      <Box>
        <FormFieldLabel required>Status</FormFieldLabel>
        <FormControl fullWidth required>
          <Select
            name="status"
            value={formData.status}
            onChange={onInputChange}
            sx={selectFieldStyles}
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status} sx={{ fontSize: '15px', py: '10px' }}>
                <Chip
                  label={status}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(status) + '20',
                    color: getStatusColor(status),
                    fontWeight: fonts.weightSemiBold,
                    fontSize: '13px',
                    height: '26px',
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Date Fields Row */}
      <Box sx={{ display: 'flex', gap: spacing.md }}>
        {/* Start Date */}
        <Box sx={{ flex: 1 }}>
          <FormFieldLabel required>Start Date</FormFieldLabel>
          <TextField
            fullWidth
            name="start_date"
            type="date"
            value={formData.start_date}
            onChange={onInputChange}
            required
            InputLabelProps={{
              shrink: true,
            }}
            sx={formFieldStyles}
          />
        </Box>

        {/* End Date */}
        <Box sx={{ flex: 1 }}>
          <FormFieldLabel required>End Date</FormFieldLabel>
          <TextField
            fullWidth
            name="end_date"
            type="date"
            value={formData.end_date}
            onChange={onInputChange}
            required
            InputLabelProps={{
              shrink: true,
            }}
            sx={formFieldStyles}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AddProjectForm;
